<template>
    <div class="posts-page">
        <page-header :title="title" :items="items" />

        <b-alert v-if="post.id" show :variant="variant">
            Post {{ post.status }}
        </b-alert>

        <div v-if="!editMode || (editMode && post.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="post"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @clear-server-error="clearServerError"
                        />
                        <history v-if="isHistory" :histories="post.histories" />
                    </div>
                </div>
            </div>
            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                v-if="!isAnswered"
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="accept"
                            >
                                <i class="fas fa-check-circle" />
                                Accept
                            </button>
                            <button
                                v-if="!isAnswered"
                                class="btn btn-danger btn-static waves-effect waves-light m-1"
                                @click="$bvModal.show('confirm-reject-modal')"
                            >
                                <i class="fa fa-times-circle" />
                                Reject
                            </button>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="post"
                            :options="{ slugBasicUrl: 'posts' }"
                            :featured-image="post.featuredImageId || null"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-featured-image-change="onFeaturedImageChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />

        <reject-modal id="confirm-reject-modal" @reject="reject" />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required, email, maxLength } from 'vuelidate/lib/validators';
import History from '@/components/posts/History';
import AddEditForm from '@/components/posts/AddEditForm';
import RejectModal from '@/components/posts/RejectModal';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage posts'
    },

    validations: {
        post: {
            title: {
                required,
                maxLength: maxLength(255)
            },
            subtitle: {
                required,
                maxLength: maxLength(255)
            },
            description: {
                required
            },
            content: {
                $each: {
                    answer: {
                        required(val, { isRequired }) {
                            if (!isRequired) {
                                return true;
                            }

                            return !!val;
                        },
                        isFormatValid(val, { regex: pattern }) {
                            if (!pattern || !val) {
                                return true;
                            }

                            const regex = new RegExp(pattern);

                            return regex.test(val);
                        },
                        email(val, { type }) {
                            if (type === 'email') {
                                return email(val);
                            }

                            return true;
                        },
                        correctQuantity(val, { settings }) {
                            if (
                                !val?.length ||
                                !settings ||
                                (!settings.minOptions && !settings.maxOptions)
                            ) {
                                return true;
                            }

                            const { minOptions: min = 0, maxOptions: max = 0 } =
                                settings;

                            return !(
                                !Array.isArray(val) ||
                                (min && val.length < min) ||
                                (max && val.length > max)
                            );
                        }
                    }
                }
            }
        }
    },

    components: {
        History,
        AddEditForm,
        RejectModal,
        ItemInformation
    },

    data() {
        const { postId } = this.$route.params;

        return {
            title: postId ? 'Edit post' : 'Add post',
            id: postId,
            editMode: !!postId,
            post: {
                content: []
            },
            serverErrors: []
        };
    },

    computed: {
        ...mapGetters({
            PENDING: 'posts/PENDING'
        }),

        items() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Boards',
                    to: `/boards`
                },
                {
                    text: 'Board',
                    to: `/boards/${this.post.boardId}/edit`
                },
                { text: this.post.id ? 'Edit' : 'Add', active: true }
            ];
        },

        sections() {
            const sections = {
                featuredImage: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        variant() {
            const { status } = this.post;

            const variants = {
                accepted: 'success',
                rejected: 'danger'
            };

            return variants[status] || 'secondary';
        },

        isAnswered() {
            return this.post.status !== this.PENDING;
        },

        isHistory() {
            return this.post.histories?.length;
        }
    },

    async created() {
        try {
            if (this.editMode) {
                this.post = await this.getPost(this.id);

                const content = [];

                for (const question of this.post.board.questions) {
                    const item = this.post.content.find(
                        ({ label }) => label === question.label
                    );

                    content.push({ ...question, answer: item?.answer || null });
                }

                this.post = { ...this.post, content };
            } else {
                const { boardId } = this.$route.params;

                const { questions } = await this.getBoard(boardId);

                this.post.content = [...questions];
            }
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            addPost: 'posts/store',
            deletePost: 'posts/destroy',
            getPost: 'posts/show',
            updatePost: 'posts/update',
            acceptPost: 'posts/accept',
            rejectPost: 'posts/reject',
            getBoard: 'boards/show'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addPost({
                        ...this.post
                    });

                    this.$router.push(
                        `/boards/${this.post.boardId}/posts/${added.id}/edit`
                    );
                } else {
                    const { updatedAt } = await this.updatePost({
                        id: this.id,
                        data: { ...this.post }
                    });

                    this.post.updatedAt = updatedAt;

                    this.$toaster('Post updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onFeaturedImageChange(imageId) {
            this.post.featuredImageId = imageId;
        },

        async onDeleteConfirm() {
            try {
                await this.deletePost(this.id);

                this.$router.push(`/boards/${this.post.boardId}/edit`);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        clearServerError(param) {
            this.serverErrors = this.serverErrors.filter(
                error => error.param !== param
            );
        },

        async accept() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                this.post = await this.acceptPost({ id: this.id });
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async reject(reason = '') {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                this.post = await this.rejectPost({ id: this.id, reason });
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        }
    }
};
</script>
